import React, { useState } from "react";
import axios from "axios";
import AlvnaBrand from "../components/AlvnaBrand";
import { endpoints } from "../apiConfig";
import { setStaffSession } from "../session";
import { colors, fonts, space, radius, buttonPrimary } from "../theme";

/**
 * Shared login screen for Waiter, Kitchen, and Manager stations. Dark
 * theme, matching order.alvna.com's staff/floor screens. Redirect
 * target is based on the STAFF MEMBER'S OWN role (returned by the
 * login endpoint), not which URL they happened to open — so a manager
 * who opens the waiter link still lands on the manager screen, same
 * as order.alvna.com's single-PIN-screen behavior.
 */
const TITLES = { waiter: "Waiter Login", kitchen: "Kitchen Login", manager: "Manager Login" };
const REDIRECTS = { waiter: "/waiter", kitchen: "/kitchen", manager: "/manager-home" };

export default function StaffLogin({ station = "waiter" }) {
  const params = new URLSearchParams(window.location.search);
  const tenantId = params.get("tenant") || "";

  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!tenantId) {
      setError("This link is missing its restaurant ID. Ask your manager for the correct link.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(endpoints.staffLogin, { tenant: tenantId, pin });
      setStaffSession(res.data.access_token, res.data.staff, res.data.tenant);
      const role = res.data.staff.role;
      window.location.href = REDIRECTS[role] || "/waiter";
    } catch (err) {
      setError(err.response?.data?.error || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.heading}>{TITLES[station] || "Staff Login"}</h2>
        {/* Reserved-height error slot so the keypad below never shifts
            position when a message appears — matches order.alvna.com's
            stabilized PIN screen. */}
        <div style={styles.errorSlot}>{error && <div style={styles.error}>{error}</div>}</div>
        <form onSubmit={submit} style={{ width: "100%" }}>
          <input
            style={styles.pinInput}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="4-digit PIN"
            maxLength={4}
            type="password"
            inputMode="numeric"
          />
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? "Checking…" : "Log In"}
          </button>
        </form>
        <div style={styles.brandFooter}>
          <AlvnaBrand variant="compact" />
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: colors.darkBg,
    padding: space[4],
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 350, // 300 content + 2x24 padding + 2x1 border: identical to the old desktop size
    boxSizing: "border-box",
    background: colors.darkSurface,
    borderRadius: radius.lg,
    border: `1px solid ${colors.darkBorder}`,
    padding: space[5],
    display: "flex",
    flexDirection: "column",
    gap: space[3],
    alignItems: "center",
  },
  heading: { margin: 0, fontFamily: fonts.display, fontSize: 18, fontWeight: 600, color: colors.darkText, textAlign: "center" },
  errorSlot: { minHeight: 20, width: "100%" },
  pinInput: {
    width: "100%",
    boxSizing: "border-box",
    textAlign: "center",
    fontFamily: fonts.display,
    fontSize: 24,
    letterSpacing: "0.3em",
    padding: "10px 0",
    borderRadius: radius.sm,
    border: `1px solid ${colors.darkBorder}`,
    background: colors.darkBg,
    color: colors.darkText,
    marginBottom: space[3],
  },
  button: { ...buttonPrimary, width: "100%" },
  brandFooter: { marginTop: space[2] },
  error: { color: "#fca5a5", fontSize: 13, textAlign: "center", fontFamily: fonts.body },
};
